import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getApiGatewayLogsFiltered,
  getDatabaseLogsFiltered,
  getBankserverLogsFiltered,
} from "@/shared/api/services/infrastructure";
import { resolveRangeMs, getBucketSizeMs } from "@/shared/utils/infrastructureUtils";

const QUERY_CONFIG = { refetchInterval: 5000, staleTime: 3000 };

const makePerformanceQuery = (key, fetchFn, range) => ({
  queryKey: [key, range.startDate, range.endDate],
  queryFn: () => fetchFn(range.startDate, range.endDate, 2000),
  ...QUERY_CONFIG,
});

export default function useInfrastructurePerformance(timeRange, resourceSnapshots) {
  const performanceRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - resolveRangeMs(timeRange));
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }, [timeRange]);

  const { data: perfApiLogs, refetch: refreshPerfApi } = useQuery(
    makePerformanceQuery("performanceApiGatewayLogs", getApiGatewayLogsFiltered, performanceRange)
  );
  const { data: perfDbLogs, refetch: refreshPerfDb } = useQuery(
    makePerformanceQuery("performanceDatabaseLogs", getDatabaseLogsFiltered, performanceRange)
  );
  const { data: perfBankLogs, refetch: refreshPerfBank } = useQuery(
    makePerformanceQuery("performanceBankserverLogs", getBankserverLogsFiltered, performanceRange)
  );

  const performanceData = useMemo(() => {
    const rangeMs = resolveRangeMs(timeRange);
    const bucketSizeMs = getBucketSizeMs(timeRange);
    const now = Date.now();
    const cutoff = now - rangeMs;

    const alignedCutoff = Math.floor(cutoff / bucketSizeMs) * bucketSizeMs;
    const alignedNow = Math.floor(now / bucketSizeMs) * bucketSizeMs;

    const buckets = new Map();
    for (let ts = alignedCutoff; ts <= alignedNow; ts += bucketSizeMs) {
      buckets.set(ts, { ts, requests: 0, latencySum: 0, latencyCount: 0, cpuSum: 0, cpuCount: 0, memorySum: 0, memoryCount: 0 });
    }

    const allLogs = [
      ...(perfApiLogs?.logs || []),
      ...(perfDbLogs?.logs || []),
      ...(perfBankLogs?.logs || []),
    ];

    const formatLabel = (ts) => {
      const d = new Date(ts);
      return timeRange === "7d"
        ? d.toLocaleDateString([], { month: "short", day: "numeric" })
        : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    allLogs.forEach((log) => {
      const createdAt = new Date(log?.createdAt || 0).getTime();
      if (!Number.isFinite(createdAt) || createdAt < cutoff || createdAt > now) return;
      const bucket = buckets.get(Math.floor(createdAt / bucketSizeMs) * bucketSizeMs);
      if (!bucket) return;
      bucket.requests += 1;
      const latency = Number(log?.latencyMs);
      if (Number.isFinite(latency)) {
        bucket.latencySum += latency;
        bucket.latencyCount += 1;
      }
    });

    resourceSnapshots.forEach((snap) => {
      if (!snap || snap.ts < cutoff || snap.ts > now) return;
      const bucket = buckets.get(Math.floor(snap.ts / bucketSizeMs) * bucketSizeMs);
      if (!bucket) return;
      const cpu = Number(snap.cpu);
      const mem = Number(snap.memory);
      if (Number.isFinite(cpu)) {
        bucket.cpuSum += cpu;
        bucket.cpuCount += 1;
      }
      if (Number.isFinite(mem)) {
        bucket.memorySum += mem;
        bucket.memoryCount += 1;
      }
    });

    let lastCpu = 0, lastMemory = 0;
    return Array.from(buckets.values()).map((b) => {
      const cpu = b.cpuCount > 0 ? Math.round(b.cpuSum / b.cpuCount) : lastCpu;
      const memory = b.memoryCount > 0 ? Math.round(b.memorySum / b.memoryCount) : lastMemory;
      const latency = b.latencyCount > 0 ? Math.round(b.latencySum / b.latencyCount) : 0;
      lastCpu = cpu;
      lastMemory = memory;
      return { ts: b.ts, time: formatLabel(b.ts), cpu, memory, requests: b.requests, latency };
    });
  }, [perfApiLogs, perfDbLogs, perfBankLogs, resourceSnapshots, timeRange]);

  return {
    performanceData,
    refetchers: {
      refreshPerfApi,
      refreshPerfDb,
      refreshPerfBank,
    },
  };
}
